import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Key, Plus, Search, Copy, Trash2,
  CheckCircle, Clock, AlertCircle, Brain, Users, Network
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface AssessmentCode {
  id: string;
  code: string;
  isUsed: boolean;
  usedByName?: string;
  autoAiAnalysis: boolean;
  createdAt: Date;
  usedAt?: Date;
  expiresAt?: Date;
  teamId: string | null;
  teamName: string | null;
  maxUses: number | null;
  useCount: number;
}

interface TeamOption {
  id: string;
  name: string;
}

export function CoachAssessmentCodes() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState<AssessmentCode[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'available' | 'used' | 'team'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [coachHasAIAccess, setCoachHasAIAccess] = useState(false);
  const [hasTeamsAccess, setHasTeamsAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [maxAssessmentCodes, setMaxAssessmentCodes] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    quantity: 1,
    autoAiAnalysis: false,
    teamId: '',
    maxUses: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/login');
          return;
        }

        const aiAccess = await checkCoachAIAccess(user.id);
        setCoachHasAIAccess(aiAccess);

        const { data: coach } = await supabase
          .from('coaches')
          .select('id, teams_access_enabled, max_assessment_codes')
          .eq('user_id', user.id)
          .maybeSingle() as any;

        if (!coach) {
          navigate('/login');
          return;
        }

        setCoachId(coach.id);
        setHasTeamsAccess(coach.teams_access_enabled === true);
        setMaxAssessmentCodes(coach.max_assessment_codes ?? null);
        await loadCodes(coach.id);

        if (coach.teams_access_enabled) {
          const { data: teamsData } = await supabase
            .from('teams')
            .select('id, name')
            .eq('coach_id', coach.id)
            .eq('is_active', true)
            .order('name');
          setTeams((teamsData || []).map(t => ({ id: t.id, name: t.name })));
        }
      } catch (err) {
        console.error('Error initializing:', err);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadCodes = async (cId: string) => {
    try {
      const { data: codesData } = await supabase
        .from('assessment_codes')
        .select('*, users!assessment_codes_used_by_fkey(full_name, email), teams(name)')
        .eq('coach_id', cId)
        .order('created_at', { ascending: false });

      const mapped: AssessmentCode[] = (codesData || []).map((code: any) => ({
        id: code.id,
        code: code.code,
        isUsed: code.is_used || false,
        usedByName: code.users?.full_name || code.users?.email || undefined,
        autoAiAnalysis: code.auto_ai_analysis || false,
        createdAt: new Date(code.created_at || Date.now()),
        usedAt: code.used_at ? new Date(code.used_at) : undefined,
        expiresAt: code.expires_at ? new Date(code.expires_at) : undefined,
        teamId: code.team_id,
        teamName: code.teams?.name || null,
        maxUses: code.max_uses,
        useCount: code.use_count ?? 0,
      }));

      setCodes(mapped);
    } catch (err) {
      console.error('Error loading codes:', err);
      setError('Failed to load assessment codes');
    }
  };

  const generateCode = () => {
    return 'HPOS' + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId) return;
    setError(null);
    setIsSubmitting(true);

    try {
      // Enforce assessment code limit
      if (maxAssessmentCodes !== null) {
        const quantity = formData.teamId ? 1 : formData.quantity;
        if (codes.length + quantity > maxAssessmentCodes) {
          setError(`You have reached your assessment code limit (${maxAssessmentCodes}). Contact your partner to increase this limit.`);
          setIsSubmitting(false);
          return;
        }
      }

      const teamId = formData.teamId || null;
      const maxUses = formData.maxUses ? parseInt(formData.maxUses) : null;

      if (teamId) {
        // Team code: generate one code linked to the team
        const { error: insertError } = await supabase
          .from('assessment_codes')
          .insert({
            code: generateCode(),
            coach_id: coachId,
            is_used: false,
            auto_ai_analysis: formData.autoAiAnalysis,
            team_id: teamId,
            max_uses: maxUses,
          });

        if (insertError) throw insertError;
      } else {
        // Individual codes: generate quantity
        const codesToInsert = Array.from({ length: formData.quantity }, () => ({
          code: generateCode(),
          coach_id: coachId,
          is_used: false,
          auto_ai_analysis: formData.autoAiAnalysis,
        }));

        const { error: insertError } = await supabase
          .from('assessment_codes')
          .insert(codesToInsert);

        if (insertError) throw insertError;
      }

      await loadCodes(coachId);
      setShowAddModal(false);
      setFormData({ quantity: 1, autoAiAnalysis: false, teamId: '', maxUses: '' });
    } catch (err: any) {
      console.error('Error generating codes:', err);
      setError(err.message || 'Failed to generate codes');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (codeId: string) => {
    if (!confirm('Are you sure you want to delete this code?')) return;
    if (!coachId) return;

    try {
      const { error: deleteError } = await supabase
        .from('assessment_codes')
        .delete()
        .eq('id', codeId);

      if (deleteError) throw deleteError;
      await loadCodes(coachId);
    } catch (err) {
      console.error('Error deleting code:', err);
      setError('Failed to delete code');
    }
  };

  const copyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const filteredCodes = codes.filter(code => {
    const matchesSearch =
      code.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      code.usedByName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      code.teamName?.toLowerCase().includes(searchQuery.toLowerCase());

    if (filter === 'available') return matchesSearch && !code.isUsed && !code.teamId;
    if (filter === 'used') return matchesSearch && code.isUsed;
    if (filter === 'team') return matchesSearch && !!code.teamId;
    return matchesSearch;
  });

  const availableCount = codes.filter(c => !c.isUsed && !c.teamId).length;
  const usedCount = codes.filter(c => c.isUsed).length;
  const teamCodeCount = codes.filter(c => !!c.teamId).length;

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading assessment codes..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-warm-500 rounded-xl shadow-soft">
                <Key className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Assessment Codes</h1>
                <p className="freud-text-sm text-chocolate-500">
                  {availableCount} individual / {teamCodeCount} team / {usedCount} used
                  {maxAssessmentCodes !== null && (
                    <span className={`ml-2 font-semibold ${codes.length >= maxAssessmentCodes ? 'text-coral-600' : 'text-chocolate-500'}`}>
                      · {codes.length} / {maxAssessmentCodes} total
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              disabled={maxAssessmentCodes !== null && codes.length >= maxAssessmentCodes}
              className="freud-btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={maxAssessmentCodes !== null && codes.length >= maxAssessmentCodes ? `Code limit reached (${maxAssessmentCodes})` : undefined}
            >
              <Plus className="w-5 h-5" />
              Generate Codes
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Search codes or teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'available', 'team', 'used'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                    filter === f
                      ? 'bg-sage-600 text-white'
                      : 'bg-cream-200 text-chocolate-600 hover:bg-cream-300'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Code</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Status</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Team / Used By</th>
                  {coachHasAIAccess && (
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Auto AI</th>
                  )}
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Created</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-cream-200 rounded font-mono text-sm text-chocolate-900">
                        {code.code}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      {code.teamId ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                          <Network className="w-3 h-3" />
                          {code.maxUses
                            ? `${code.useCount} / ${code.maxUses}`
                            : `${code.useCount} used`}
                        </span>
                      ) : code.isUsed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-coral-100 text-coral-700 rounded-full text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Used
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-sm">
                          <Clock className="w-3 h-3" />
                          Available
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-chocolate-600">
                      {code.teamId ? (
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          <Users className="w-3.5 h-3.5 text-sage-600" />
                          {code.teamName || 'Team'}
                        </span>
                      ) : (
                        code.usedByName || '--'
                      )}
                    </td>
                    {coachHasAIAccess && (
                      <td className="px-6 py-4 text-center">
                        {code.autoAiAnalysis ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-xs font-medium">
                            <Brain className="w-3 h-3" />
                            On
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Off</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 text-chocolate-600 text-sm">
                      {code.createdAt.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => copyCode(code.code)}
                          className="p-2 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
                          title="Copy code"
                        >
                          {copiedCode === code.code ? (
                            <CheckCircle className="w-4 h-4 text-sage-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        {!code.isUsed && code.useCount === 0 && (
                          <button
                            onClick={() => handleDelete(code.id)}
                            className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                            title="Delete code"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCodes.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {codes.length === 0 ? 'No assessment codes yet. Generate your first codes above.' : 'No codes match your search.'}
            </div>
          )}
        </div>
      </main>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="text-xl font-bold text-chocolate-900 mb-6">
              Generate Assessment Codes
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              {hasTeamsAccess && teams.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">
                    Link to Team (optional)
                  </label>
                  <select
                    value={formData.teamId}
                    onChange={(e) => setFormData({ ...formData, teamId: e.target.value, quantity: e.target.value ? 1 : formData.quantity })}
                    className="freud-input"
                  >
                    <option value="">No team (individual codes)</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-chocolate-400 mt-1">
                    {formData.teamId
                      ? 'One reusable code will be created. All respondents using it auto-join this team.'
                      : 'Individual codes are single-use per respondent.'}
                  </p>
                </div>
              )}

              {formData.teamId && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">
                    Max Uses (optional)
                  </label>
                  <input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                    min="1"
                    placeholder="Unlimited"
                    className="freud-input"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">
                    Leave blank for unlimited uses, or set a cap
                  </p>
                </div>
              )}

              {!formData.teamId && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">
                    Number of Codes
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    min="1"
                    max="100"
                    required
                    className="freud-input"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">Generate up to 100 codes at once</p>
                </div>
              )}

              {coachHasAIAccess && (
                <div className="bg-cream-100 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-sage-100 rounded-lg">
                        <Brain className="w-5 h-5 text-sage-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-chocolate-800">Auto AI Analysis</p>
                        <p className="text-xs text-chocolate-500">
                          Respondents get instant AI analysis after completing their assessment
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, autoAiAnalysis: !formData.autoAiAnalysis })}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          formData.autoAiAnalysis ? 'bg-sage-500' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                            formData.autoAiAnalysis ? 'translate-x-[22px]' : 'translate-x-0.5'
                          }`}
                        />
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({ quantity: 1, autoAiAnalysis: false, teamId: '', maxUses: '' });
                  }}
                  className="freud-btn-secondary flex-1"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="freud-btn-primary flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
